import React from 'react';
import type { IPublicationExtended } from '@typings/api/IPublication';
import { usePageProps } from '@lib/PagePropsContext';
import type { IPublicationFilters } from '@typings/IPublicationFilters';
import { useHistoryManager } from '@client/lib/HistoryManager';

import { defaultFilters } from '../Index.const';
import type { IIndexProps } from '../Index.typings';
import { itemsReducer } from './itemsReducer';
import { requestItems } from './requestItems';
import { useCityList } from './useCityList';

const emptyArray: IPublicationExtended[] = [];

export function usePublications() {
    const { data } = usePageProps<IIndexProps>();
    const { setPageProps } = useHistoryManager<IIndexProps>();

    const [state, dispatch] = React.useReducer(itemsReducer, {
        items: data?.publications ?? emptyArray,
        loading: data === undefined,
        isAllLoaded: data !== undefined ? data.publications.length >= data.countPublications : false,
        filters: data?.filters ?? defaultFilters,
        countPublications: data?.countPublications ?? 0,
    });

    const cities = useCityList(state.filters.country);

    React.useEffect(() => {
        if (data === undefined || data.publications === state.items) {
            return;
        }

        dispatch({
            type: 'init',
            items: data.publications,
            count: data.countPublications,
            filters: data.filters,
        });
    }, [data?.publications]);

    const resetItems = React.useCallback(() => dispatch({ type: 'clearItems' }), [dispatch]);

    const resetFilters = React.useCallback(() => {
        dispatch({ type: 'resetFilters' });
    }, [dispatch]);

    const request = React.useCallback(() => {
        dispatch({ type: 'clearItems' });

        requestItems(state.filters, 0).then(({ count, items }) => {
            dispatch({ type: 'replace', items, count });
        });
    }, [dispatch, state.filters]);

    const loadNext = React.useCallback(() => {
        if (state.loading || state.isAllLoaded) {
            return;
        }

        dispatch({ type: 'setLoading' });

        requestItems(state.filters, state.items.length).then(({ count, items }) => {
            dispatch({ type: 'append', items, count });

            setPageProps({
                publications: state.items.concat(items),
                countPublications: count,
                countries: data?.countries ?? [],
                filters: state.filters,
                likes: data?.likes,
                pageInfo: data?.pageInfo ?? { title: '' },
            });
        });
    }, [state.items, state.filters, state.loading, state.isAllLoaded]);

    const setFilter = React.useCallback(<K extends keyof IPublicationFilters>(key: K, value: IPublicationFilters[K]) => {
        dispatch({ type: 'setFilter', key, value });
    }, [dispatch]);

    return {
        publications: state.items,
        loading: state.loading,
        request,
        loadNext,
        resetItems,
        countries: data?.countries,
        filters: state.filters,
        cities,
        setFilter,
        resetFilters,
        isAllLoaded: state.isAllLoaded,
    };
}
